import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Grid, Card, CardContent, Typography, Chip, Box } from '@mui/material';
import { CellTowerIcon as CellTower, CloudDoneOutlinedIcon as CloudDoneOutlined, CloudOffOutlinedIcon as CloudOffOutlined, SpeedIcon as Speed, WifiIcon as Wifi, WifiOffIcon as WifiOff } from "../../../icons";
import { useRootStore } from '../../../store/contexts';

export const NetworkTab: React.FC = observer(() => {
  const rootStore = useRootStore();
  const monitorStore = rootStore.systemMonitorStore;
  const eventSourceStore = rootStore.eventSourceStore;
  const langStore = rootStore.langStore;

  const [swState, setSwState] = useState<string>('checking');
  const [swUpdateAvailable, setSwUpdateAvailable] = useState<boolean>(false);

  useEffect(() => {
    const checkServiceWorker = async () => {
      if (!('serviceWorker' in navigator)) {
        setSwState('not-supported');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          setSwState(registration.active?.state || 'unknown');
          setSwUpdateAvailable(!!registration.waiting);
        } else {
          setSwState('not-registered');
        }
      } catch (error) {
        setSwState('error');
      }
    };

    checkServiceWorker();

    const interval = setInterval(checkServiceWorker, 5000);
    return () => clearInterval(interval);
  }, []);

  const getConnectionColor = () => {
    if (eventSourceStore.connectionState === 'connected' && monitorStore.networkInfo.online) {
      return 'success';
    }
    if (eventSourceStore.connectionState === 'connecting') {
      return 'warning';
    }
    return 'error';
  };

  const getConnectionIcon = () => {
    if (eventSourceStore.connectionState === 'connected' && monitorStore.networkInfo.online) {
      return <CloudDoneOutlined color="success" sx={{ fontSize: 40 }} />;
    }
    return <CloudOffOutlined color="error" sx={{ fontSize: 40 }} />;
  };

  const getNetworkTypeColor = () => {
    const type = monitorStore.networkInfo.effectiveType;
    if (type === '4g') return 'success';
    if (type === '3g') return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {monitorStore.networkInfo.online ? (
                  <Wifi color="success" sx={{ mr: 1 }} />
                ) : (
                  <WifiOff color="error" sx={{ mr: 1 }} />
                )}
                <Typography variant="h6">{langStore.monitorBrowserConnection}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getConnectionIcon()}
              </Box>
              <Chip
                label={monitorStore.networkInfo.online ? langStore.monitorOnline.toUpperCase() : langStore.monitorOffline.toUpperCase()}
                color={monitorStore.networkInfo.online ? 'success' : 'error'}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {langStore.monitorSSEStatus}: {eventSourceStore.connectionState}
              </Typography>
              {eventSourceStore.reconnectAttempts > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {langStore.monitorReconnectAttempts}: {eventSourceStore.reconnectAttempts}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CellTower color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorNetworkType}</Typography>
              </Box>
              <Chip
                label={monitorStore.networkInfo.effectiveType.toUpperCase()}
                color={getNetworkTypeColor()}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorDownlink}: {monitorStore.networkInfo.downlink.toFixed(2)} {langStore.monitorMbps}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorRTT}: {monitorStore.networkInfo.rtt} {langStore.monitorMs}
              </Typography>
              {monitorStore.networkInfo.saveData && (
                <Chip label={langStore.monitorDataSaverMode} color="info" size="small" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorServiceWorker}</Typography>
              </Box>
              <Chip
                label={swState === 'checking' ? langStore.monitorChecking.toUpperCase() :
                       swState === 'activated' ? langStore.monitorSWActivatedState.toUpperCase() :
                       swState === 'not-supported' ? langStore.monitorSWNotSupportedState.toUpperCase() :
                       swState === 'not-registered' ? langStore.monitorSWNotRegisteredState.toUpperCase() :
                       swState === 'error' ? langStore.monitorSWErrorState.toUpperCase() :
                       swState === 'unknown' ? langStore.monitorSWUnknownState.toUpperCase() :
                       swState.toUpperCase()}
                color={swState === 'activated' ? 'success' : swState === 'not-supported' || swState === 'error' ? 'error' : 'default'}
                sx={{ mb: 1 }}
              />
              {swUpdateAvailable && (
                <Chip label={langStore.monitorUpdateAvailable} color="warning" sx={{ ml: 1, mb: 1 }} />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {swState === 'not-supported' && langStore.monitorSWNotSupported}
                {swState === 'not-registered' && langStore.monitorSWNotRegistered}
                {swState === 'activated' && langStore.monitorSWActivated}
                {swState === 'error' && langStore.monitorSWError}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudDoneOutlined color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorSSEConnection}</Typography>
              </Box>
              <Chip
                label={eventSourceStore.connectionState.toUpperCase()}
                color={getConnectionColor()}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorRealTimeConnectionDesc}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
});
